import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HeaderProps } from './Header.types';
import HeaderView from './Header.view';
import { RouteEnum } from '../../routes/routeEnum';
import { paths } from '../../routes/paths';
import useHeader from './useHeader';
import { useHeaderMenu } from './useHeaderMenu';
import { useGetNotificationsCountQuery } from '../../../domains/notifications/infrastructure/api/notifications.api';

/**
 * Header Component
 * Top application header following DDD principles
 *
 * DDD Layer: UI (Presentation)
 * Responsibilities:
 * - User interaction handling
 * - State management for UI
 * - Navigation logic
 * - Integration with Redux store
 */
const Header: React.FC<HeaderProps> = ({
  className,
  showProfile = true,
  showNotifications = true,
  showLanguageSelector = false,
  showSearch = false,
  logo,
  onLogoClick,
  navigationItems,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    onSearch,
    searchQuery,
    searchResults,
    isSearchLoading,
    fetchHeaderSuggestions,
  } = useHeader();

  // Get user role from Redux
  const userRole = useSelector(
    (state: any) => state.authorization?.user?.role || 'none'
  );

  // Get user menu items
  const { userMenuItems } = useHeaderMenu({ userRole });

  // Local UI state
  const [isMenuOpen, setMenuOpen] = useState(false);

  // Notifications count — polls every 60s
  const { data: notificationsCountData } = useGetNotificationsCountQuery(undefined, {
    pollingInterval: 60_000,
    skip: !useSelector((state: any) => state.authorization?.user),
  });
  const notificationsCount = notificationsCountData?.count ?? 0;

  // Get data from Redux store
  const user = useSelector((state: any) => state.authorization?.user);
  const isAdmin = useSelector((state: any) => state.authorization?.isAdmin);
  const company = useSelector((state: any) => state.dynamic?.company);

  // Handlers
  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else {
      navigate(RouteEnum.DASHBOARD);
    }
  };

  const handleProfileMenuToggle = () => {
    setMenuOpen(!isMenuOpen);
  };

  const handleNotificationsNavigate = () => {
    navigate(`/owner${paths.notifications}`);
  };


  // Prepare user profile data
  const userProfile = user
    ? {
        id: user.id,
        fullName: user.fullName || `${user.firstName} ${user.lastName}`,
        email: user.email,
        avatar: user.avatar,
        role: isAdmin ? 'owner' : 'tenant',
      }
    : null;

  // Prepare company data
  const companyData = company
    ? {
        name: company.name,
        logo: company.logo,
      }
    : null;

  // Get language for search
  const lang = i18n.language as 'en' | 'ua';

  // Pass props to view
  const viewProps = {
    className,
    showProfile,
    showNotifications,
    showLanguageSelector,
    showSearch,
    logo: logo || companyData?.logo,
    onLogoClick: handleLogoClick,
    navigationItems,
    userProfile,
    userMenuItems,
    companyData,
    isMenuOpen,
    onProfileMenuToggle: handleProfileMenuToggle,
    onNotificationsClick: handleNotificationsNavigate,
    notificationsCount,
    t,
    // Search props
    onSearch,
    searchQuery,
    searchResults,
    isSearchLoading,
    fetchHeaderSuggestions,
    lang,
  };

  return <HeaderView {...viewProps} />;
};

export default Header;
